// Поле-связь: выбрать другую карточку поиском.
//
// ПОЧЕМУ НЕ ВЫПАДАЮЩИЙ СПИСОК. Компаний и контактов в зеркале десятки тысяч — список
// целиком не загрузить, а загруженный не пролистать. Поэтому поиск по подстроке.
//
// ЧЕМ ОТЛИЧАЕТСЯ ОТ components/EntityPicker.tsx. Тот ходит в ядро напрямую через свой хук
// и знает ровно четыре сущности Bitrix. Этот берёт данные у ИСТОЧНИКА карточки: сегодня
// поддельного, завтра — своей схемы, где сущностей девять и Bitrix среди них нет. Когда
// транспорт появится, EntityPicker станет частным случаем этого поля, и его можно будет
// свести сюда — но не раньше, чем у своей схемы появится поиск.
//
// ЧТО ЗДЕСЬ ВАЖНО ДЛЯ ЗАПОЛНЕНИЯ. Выбранная карточка показывается НАЗВАНИЕМ и подсказкой
// (ИНН, IMO, стадия) — по ним человек видит, что выбрал то самое, а не тёзку. Ссылка
// «перейти» рядом: карточку часто открывают, чтобы свериться, и возвращаться в форму
// через меню никто не станет.

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import type { ИсточникКарточек } from './source'
import type { Сущность, Ярлык } from './types'

/** Задержка перед запросом: человек печатает, незачем дёргать источник на каждую букву. */
const ЗАДЕРЖКА_МС = 250

/** Короче двух знаков искать бессмысленно: вернётся половина базы. */
const МИН_ЗАПРОС = 2

interface Props {
  сущность: Сущность
  значение: Ярлык | null
  onChange: (v: Ярлык | null) => void
  источник: ИсточникКарточек
  id: string
  путь?: (id: string) => string
  подсказкаВвода?: string
  disabled?: boolean
  /** Ошибка поля: рамку красит карточка, здесь только связь с текстом ошибки. */
  ошибкаId?: string
}

export function RelationPicker({
  сущность, значение, onChange, источник, id, путь, подсказкаВвода, disabled, ошибкаId,
}: Props) {
  const [текст, setТекст] = useState('')
  const [задержанный, setЗадержанный] = useState('')
  const [открыт, setОткрыт] = useState(false)
  const [строки, setСтроки] = useState<Ярлык[]>([])
  const [ищем, setИщем] = useState(false)
  const [отказ, setОтказ] = useState(false)
  const [активная, setАктивная] = useState(0)
  const рамка = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const т = setTimeout(() => setЗадержанный(текст), ЗАДЕРЖКА_МС)
    return () => clearTimeout(т)
  }, [текст])

  // Клик вне поля закрывает список — иначе он висит поверх страницы.
  useEffect(() => {
    const вне = (е: MouseEvent) => {
      if (рамка.current && !рамка.current.contains(е.target as Node)) setОткрыт(false)
    }
    document.addEventListener('mousedown', вне)
    return () => document.removeEventListener('mousedown', вне)
  }, [])

  const короткий = задержанный.trim().length > 0 && задержанный.trim().length < МИН_ЗАПРОС

  useEffect(() => {
    let брошен = false
    const q = задержанный.trim()
    if (q.length < МИН_ЗАПРОС) { setСтроки([]); setОтказ(false); return () => { брошен = true } }
    setИщем(true)
    setОтказ(false)
    источник.поиск(сущность, q)
      .then((р) => { if (!брошен) { setСтроки(р); setАктивная(0) } })
      .catch(() => { if (!брошен) { setСтроки([]); setОтказ(true) } })
      .finally(() => { if (!брошен) setИщем(false) })
    return () => { брошен = true }
  }, [задержанный, сущность, источник])

  const выбрать = (я: Ярлык) => {
    onChange(я)
    setТекст('')
    setЗадержанный('')
    setОткрыт(false)
  }

  const поКлавише = (е: React.KeyboardEvent) => {
    if (!открыт || !строки.length) return
    if (е.key === 'ArrowDown') { е.preventDefault(); setАктивная((i) => Math.min(i + 1, строки.length - 1)) }
    if (е.key === 'ArrowUp') { е.preventDefault(); setАктивная((i) => Math.max(i - 1, 0)) }
    if (е.key === 'Enter') { е.preventDefault(); выбрать(строки[активная]) }
    if (е.key === 'Escape') setОткрыт(false)
  }

  const адрес = useMemo(
    () => (значение && путь ? путь(значение.id) : null),
    [значение, путь],
  )

  if (значение) {
    return (
      <div className="picker">
        <div className="picked">
          <span className="picked-title" title={значение.название}>{значение.название}</span>
          {значение.подсказка && <span className="muted picked-hint">{значение.подсказка}</span>}
          {адрес && (
            <Link className="picked-go" to={адрес} title={`Открыть карточку: ${значение.название}`}>
              Перейти
            </Link>
          )}
          {!disabled && (
            <button
              type="button"
              className="picked-clear"
              onClick={() => onChange(null)}
              aria-label={`Отвязать: ${значение.название}`}
            >
              ×
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="picker" ref={рамка}>
      <input
        id={id}
        value={текст}
        disabled={disabled}
        placeholder={подсказкаВвода ?? 'Начните вводить название…'}
        onChange={(е) => { setТекст(е.target.value); setОткрыт(true) }}
        onFocus={() => setОткрыт(true)}
        onKeyDown={поКлавише}
        autoComplete="off"
        role="combobox"
        aria-expanded={открыт}
        aria-controls={`${id}-list`}
        aria-describedby={ошибкаId}
      />
      {открыт && (
        <div className="picker-drop" id={`${id}-list`} role="listbox">
          {короткий && (
            <div className="picker-note muted">Введите хотя бы два знака</div>
          )}
          {!короткий && ищем && <div className="picker-note muted">Ищем…</div>}
          {!короткий && !ищем && отказ && (
            <div className="picker-note err">Поиск недоступен: источник не ответил</div>
          )}
          {!короткий && !ищем && !отказ && !строки.length && !!задержанный.trim() && (
            <div className="picker-note muted">Ничего не найдено</div>
          )}
          {строки.map((я, i) => (
            <button
              type="button"
              key={я.id}
              role="option"
              aria-selected={i === активная}
              className={`picker-row${i === активная ? ' active' : ''}`}
              onMouseEnter={() => setАктивная(i)}
              onClick={() => выбрать(я)}
            >
              <span className="picker-row-title">{я.название}</span>
              {я.подсказка && <span className="muted picker-row-hint">{я.подсказка}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
